import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Modal from '@/components/common/Modal'
import { getJsonRaw } from '@/api/generateapp'
import { useProjectStore } from '@/store/projectStore'
import { refreshJourney } from '@/store/journeyStore'
import type { JourneyTab } from '@/api/journey'
import type { RawAppData } from '@/types'
import TeamPanel from '../TeamTab'
import SignInRulesPanel from '../settings/SignInRulesPanel'

/**
 * People (and a private website's Access): the Team panel's parts in the
 * People layout, plus "How people sign in" (a summary of application.auth;
 * Change opens the SAME AuthConfigEditor in a drawer).
 */
export default function PeopleTab({ tab }: { tab?: JourneyTab }) {
  const project = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const [params, setParams] = useSearchParams()
  const openInvite = params.get('invite') === '1'
  const [auth, setAuth] = useState<RawAppData['application']['auth'] | null>(null)
  const [drawer, setDrawer] = useState(false)

  useEffect(() => {
    if (!openInvite) return
    const next = new URLSearchParams(params)
    next.delete('invite')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInvite])

  const loadAuth = () => {
    if (!project) return
    getJsonRaw(project)
      .then((d) => setAuth(((d as RawAppData).application.auth) || null))
      .catch((err) => { console.error('[people] could not read sign-in settings', err); setAuth(null) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadAuth, [project])

  const a = (auth || {}) as { requireLogin?: boolean; providers?: string[]; mfa?: { mode?: string }; allowRegistration?: boolean }
  const summary = [
    (a.providers || ['project-users']).map((p) => (p === 'project-users' ? 'Email and password' : p)).join(', '),
    a.mfa?.mode === 'required' ? 'two-step sign-in required' : a.mfa?.mode === 'off' ? 'no two-step sign-in' : 'two-step sign-in optional',
    a.requireLogin ? 'signing in is required' : 'public pages open without signing in',
    a.allowRegistration ? 'visitors may sign themselves up' : '',
  ].filter(Boolean).join(' · ')

  const title = tab?.label || 'People'
  return (
    <div className="swb swb-page">
      <div className="swb-head">
        <div>
          <h1>{title}</h1>
          <p>{tab?.intro || 'Who helps build the app, and who will use it once it is live.'}</p>
        </div>
      </div>
      <TeamPanel
        variant="people"
        openInvite={openInvite}
        onAfterMutate={refreshJourney}
        renderSignIn={() => (
          <section className="swb-card swb-card-pad" aria-labelledby="ppl-signin"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <h2 id="ppl-signin" className="swb-section-title">How people sign in</h2>
              <span style={{ fontSize: 14 }}>{summary}.</span>
              <span className="swb-muted">What each person can do comes from what they do in the app.</span>
            </span>
            <button type="button" className="swb-btn swb-btn-secondary" onClick={() => setDrawer(true)}>Change</button>
          </section>
        )}
      />
      {drawer && (
        <Modal open size="wide" title="How people sign in" onClose={() => setDrawer(false)}>
          <SignInRulesPanel onSaved={() => { loadAuth(); setDrawer(false) }} />
        </Modal>
      )}
    </div>
  )
}
